import routes from 'js/core_pre_webpack/routes';
import React, {
  useCallback,
  useState,
  KeyboardEvent,
  useEffect,
  MouseEvent,
  useRef,
} from 'react';
import styled from 'astroturf/react';
import { Paper } from 'js/social/shared/types';
import { Body } from 'js/design_system/Body';
import { Menu } from 'js/design_system/Menu';
import { SearchIcon } from 'js/social/shared/Icons';
import { usePaperSearch } from 'js/social/shared/attachments/usePaperSearch';
import { Spinner } from 'js/academia_components/transients/spinner';
import { PopoverWidthMode } from 'js/design_system/Popover';
import { mod } from 'js/social/shared/SocialEditor/utils';
import { scrollChildIntoView } from 'js/social/shared/utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;
  margin: 8px 0;
  background-color: $ds-off-white;
`;

const InputContainer = styled.div`
  @import 'stylesheets/design_system/font';

  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 8px;
  background-color: $ds-white;
  border: 1px solid $ds-gray-light;
  border-radius: 4px;
  box-shadow: inset 0 2px 4px rgb(0 0 0 / 0.04);

  input {
    @include body__medium;
    flex-grow: 1;
    border: none;
    outline: none;
  }
`;

const Link = styled.a`
  color: $ds-primary-blue;
  text-decoration: underline;
`;

const Message = styled(Body)`
  padding: 8px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.div<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  cursor: pointer;

  &.selected {
    background-color: #afccfc;
  }
`;

const PaperTitle = styled(Body)`
  @import 'stylesheets/design_system/font';
  @include serif;
  font-size: 16px;
  line-height: 20px;
  color: $ds-black;
`;

const PaperAuthors = styled(Body)`
  @import 'stylesheets/design_system/font';
  @include sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: $ds-gray-dark;
`;

type Props = {
  onChange: (paper: Paper) => void;
};

export const PaperSelector: React.FC<Props> = ({ onChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedDiv, setSelectedDiv] = useState<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { papers, isLoading } = usePaperSearch(search);

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    scrollChildIntoView(popoverRef.current, selectedDiv);
  }, [selectedDiv]);

  const upload = (event: MouseEvent) => {
    if (
      !confirm(
        'You will lose the text you have already entered if you upload a new paper. Do you want to continue?',
      )
    ) {
      event.preventDefault();
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex(selectedIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex(selectedIndex - 1);
      } else if (
        (event.key === 'Enter' || event.key === 'Tab') &&
        papers.length > 0
      ) {
        event.preventDefault();
        event.stopPropagation();
        const index = mod(selectedIndex, papers.length);
        onChange(papers[index]);
      }
    },
    [papers, selectedIndex, onChange],
  );

  const renderMenu = useCallback(() => {
    if (isLoading) {
      return <Spinner show />;
    } else if (papers.length <= 0) {
      return <Message size="md">No results found.</Message>;
    } else {
      return (
        <List>
          {papers.map((paper, index) => {
            const selected = mod(selectedIndex, papers.length) === index;
            return (
              <ListItem
                key={paper.id}
                ref={(ref) => selected && setSelectedDiv(ref)}
                selected={selected}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => onChange(paper)}
              >
                <PaperTitle>{paper.title}</PaperTitle>
                <PaperAuthors>
                  {paper.authors.map((p) => p.displayName).join(', ')}
                </PaperAuthors>
              </ListItem>
            );
          })}
        </List>
      );
    }
  }, [papers, isLoading, selectedIndex, onChange]);

  return (
    <Container>
      <Menu
        popoverRef={popoverRef}
        open={showMenu}
        widthMode={PopoverWidthMode.Activator}
        maxHeight={256}
        render={renderMenu}
        onChange={setShowMenu}
      >
        <InputContainer>
          <SearchIcon color="#91919E" />

          <input
            placeholder="Search for papers on Academia.edu"
            autoFocus
            value={search}
            onFocus={() => setShowMenu(true)}
            onKeyDown={handleKeyDown}
            onChange={(event) => setSearch(event.target.value)}
          />
        </InputContainer>
      </Menu>

      <Body size="md">
        Or,{' '}
        <Link href={routes.upload_funnel_url()} onClick={upload}>
          upload a paper
        </Link>{' '}
        to Academia.edu
      </Body>
    </Container>
  );
};
